import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, HardDrive, Globe, ArrowLeft, Check, X } from "lucide-react";
import { useStore } from "../context/StoreContext";
import { PageHeader, Section } from "../components/common";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";

const OFF = "hb-analytics-off";

function Row({ label, value, testid }) {
  return (
    <li className="py-2 flex justify-between gap-3 text-sm" data-testid={testid}>
      <span>{label}</span>
      <span className="num text-muted-foreground text-right">{value}</span>
    </li>
  );
}

function OnDevice() {
  const { state, hasPasscode } = useStore();
  const receipts = state.transactions.flatMap((t) => t.receipts || []);
  const mb = (receipts.reduce((s, r) => s + (r.size || 0), 0) / 1048576).toFixed(1);
  const rows = [
    ["Transactions from your statements", state.transactions.length, "transactions"],
    ["Accounts", state.accounts.length, "accounts"],
    ["Bills & recurring items", state.bills.length, "bills"],
    ["Debts", state.debts.length, "debts"],
    ["Savings goals", state.goals.length, "goals"],
    ["Receipt photos", `${receipts.length} (${mb} MB)`, "receipts"],
    ["Business trips logged", (state.mileage || []).length, "trips"],
    ["Learned receipt stores", Object.keys(state.vendorLinks || {}).length, "vendors"],
    ["Categorization rules", (state.rules || []).length, "rules"],
    ["Change history entries", state.audit.length, "audit"],
  ];
  return (
    <Section title="Stays on this device" subtitle={`Stored in this browser only, encrypted with AES-256 (${hasPasscode() ? "key from your passcode" : "device key"}).`} action={<HardDrive className="h-5 w-5 text-income" />} testid="privacy-on-device">
      <ul className="divide-y">{rows.map(([l, v, id]) => <Row key={id} label={l} value={v} testid={`privacy-count-${id}`} />)}</ul>
      <ul className="mt-4 space-y-1.5 text-sm text-muted-foreground">
        <li className="flex gap-2"><Check className="h-4 w-4 text-income shrink-0 mt-0.5" /> PDF and CSV statements are read in your browser. The files are never uploaded.</li>
        <li className="flex gap-2"><Check className="h-4 w-4 text-income shrink-0 mt-0.5" /> Receipt text reading (OCR) runs on this device with an engine served by this app.</li>
        <li className="flex gap-2"><Check className="h-4 w-4 text-income shrink-0 mt-0.5" /> Bill reminders and notifications are created on this device.</li>
        <li className="flex gap-2"><Check className="h-4 w-4 text-income shrink-0 mt-0.5" /> Backups and share images are created on this device and only go where you save or send them.</li>
        <li className="flex gap-2"><Check className="h-4 w-4 text-income shrink-0 mt-0.5" /> Face ID / Touch ID is checked by your device; the app never sees your face or fingerprint.</li>
        <li className="flex gap-2"><Check className="h-4 w-4 text-income shrink-0 mt-0.5" /> No bank logins, no ads, no cloud sync, no AI services.</li>
      </ul>
    </Section>
  );
}

function LeavesDevice() {
  const [on, setOn] = useState(localStorage.getItem(OFF) !== "1");
  const toggle = (v) => {
    setOn(v);
    if (v) {
      localStorage.removeItem(OFF);
      window.posthog?.opt_in_capturing?.();
      toast.success("Anonymous page views turned on. Takes full effect after the app reloads.");
    } else {
      localStorage.setItem(OFF, "1");
      window.posthog?.opt_out_capturing?.();
      toast.success("Analytics turned off on this device");
    }
  };
  return (
    <Section title="What leaves this device" subtitle="Only this one thing, and none of your financial data." action={<Globe className="h-5 w-5 text-muted-foreground" />} testid="privacy-leaves-device">
      <div className="rounded-xl border p-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-semibold text-sm">Anonymous page-view count</p>
            <p className="text-sm text-muted-foreground mt-1">The page address you open (e.g. /budget), browser and device type, and a random ID. No amounts, names or on-screen text. No screen recording or click tracking. Sent to Emergent's analytics (ap.emergent.sh).</p>
          </div>
          <div className="flex items-center gap-2 shrink-0"><Switch checked={on} onCheckedChange={toggle} data-testid="privacy-analytics-switch" /><span className="text-xs w-6" data-testid="privacy-analytics-status">{on ? "On" : "Off"}</span></div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-3 flex gap-1.5" data-testid="privacy-fonts-note"><Check className="h-3.5 w-3.5 mt-0.5 shrink-0 text-income" /> Fonts and images are served by this app itself — no Google Fonts or image CDN requests.</p>
      <p className="text-xs text-muted-foreground mt-3 flex gap-1.5"><X className="h-3.5 w-3.5 mt-0.5 shrink-0" /> Nothing else is sent: no transactions, balances, receipts, goals or statement files.</p>
    </Section>
  );
}

export default function Privacy() {
  return (
    <div data-testid="privacy-page">
      <Button variant="ghost" size="sm" asChild className="-ml-2 mb-2"><Link to="/settings" data-testid="privacy-back-link"><ArrowLeft className="h-4 w-4 mr-1" /> Settings</Link></Button>
      <PageHeader title="Privacy Center" subtitle="Exactly what stays on your device and what doesn't." />
      <div className="space-y-6">
        <OnDevice />
        <LeavesDevice />
        <p className="text-xs text-muted-foreground flex items-center gap-1.5"><ShieldCheck className="h-3.5 w-3.5" /> The app also blocks scripts from unapproved sites (Content-Security-Policy).</p>
      </div>
    </div>
  );
}
